import React, { useState, useEffect } from 'react';

import { Popover } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';

import { useDeleteEventDatesModels, useDeleteEventDate } from '@hooks/API/Event/UseEvent';

import AddressLine from '@components/Addresses/AddressLine';
import HorizontalDate from '@components/Dates/HorizontalDate';
import HorizontalPeriod from '@components/Dates/HorizontalPeriod';
import VerticalDate from '@components/Dates/VerticalDate';
import VerticalPeriod from '@components/Dates/VerticalPeriod';
import { useBookingEvent } from '@components/EventDrawer/BookingEvent/BookingEventContext';
import BookingEventSingleModelName from '@components/EventDrawer/BookingEvent/EventDatesAndModels/BookingEventSingleModelName';
import EventDateModel from '@components/EventDrawer/BookingEvent/EventDatesAndModels/EventDateModel';
import { BookingEventModelInfo } from '@components/EventDrawer/EventDrawerUi/EventUI';
import { NwButton, NwLinkButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';
import ResponsiveFlexContainer from '@components/Gui/ResponsiveFlexContainer';

import CollapsibleModels from './CollapsibleModels';
import {
    groupModels,
    sortGroupedModelsByCurrentModel,
    findModelInEventData,
    getModelActionName,
} from './eventDatesAndModelsUtils';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

export const BookingEventModelDetailsWrapper = styled.div`
    display: flex;
    width: 100%;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const BookingEventDateContainer = styled.div`
    padding: 0.75rem 0.75rem 0.75rem 1.25rem;
    width: 6.65rem;
    text-align: center;
    display: flex;
    flex-direction: column;
    border-bottom: ${(props) => props.theme.DefaultBorder};

    @media (max-width: 768px) {
        width: 100%;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: 0.25rem 2rem;
    }

    .calendar-button {
        padding-top: 0.5rem;
    }
`;

const BookingEventStyledCalendarEditIcon = styled(NwIcon)`
    cursor: pointer;
    font-size: 1.5rem;
`;

const BookingEventDateInfo = styled.div`
    padding: 0 0 2rem 2rem;

    @media only screen and (max-width: 720px) {
        padding: 0 0 1rem 1rem;
    }

    .horizontal-period-container {
        @media only screen and (max-width: 720px) {
            justify-content: flex-start;
        }
    }
`;

const CallTimeContainerDiv = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    font-size: ${(props) => props.theme.FontSizeSm};
    margin-top: 0.5rem;

    @media only screen and (max-width: 720px) {
        align-items: flex-start;
        flex-direction: row;
        gap: 0.5rem;
        padding-bottom: 1rem;
    }

    .call-time-label {
        text-transform: uppercase;
        font-weight: bold;
    }

    .call-time-value {
        font-weight: normal;
    }
`;
const CallTimeContainer = ({ callTime }) => {
    return (
        <CallTimeContainerDiv className="call-time-container">
            <div className="call-time-label">CALL TIME</div>
            <div className="call-time-value">{callTime}</div>
        </CallTimeContainerDiv>
    );
};

const EventDatesAndModels = ({
    eventData,
    eventDateInfo,
    selectedModel,
    loadEventData,
    filterModel,
    afterUpdate,
    onExtendOptionNumberToAllDates,
    onOpenEditWorkingDateDrawer,
    onOpenEditWorkingDayDrawer,
}) => {
    const _groupedModels = groupModels(eventDateInfo.EventDatesModels, {
        FromDate: eventDateInfo.FromDate,
        ToDate: eventDateInfo.ToDate,
        StartBreak: eventDateInfo.StartBreak,
        EndBreak: eventDateInfo.EndBreak,
        Address: eventDateInfo.Address,
        CallTime: eventDateInfo.CallTime,
    });

    const filteredGroupedModels = sortGroupedModelsByCurrentModel(_groupedModels, selectedModel);
    const [groupedModels, setGroupedModels] = useState(filteredGroupedModels);
    const { mutateAsync: onDeleteEventDatesModels } = useDeleteEventDatesModels();
    const { mutateAsync: onDeleteEventDate } = useDeleteEventDate();
    const [viewDateModels, setViewDateModels] = useState(false);
    const { isMobile, breakpoint } = useBookingEvent();

    useEffect(() => {
        const _groupedModels = groupModels(eventDateInfo.EventDatesModels, {
            FromDate: eventDateInfo.FromDate,
            ToDate: eventDateInfo.ToDate,
            StartBreak: eventDateInfo.StartBreak,
            EndBreak: eventDateInfo.EndBreak,
            Address: eventDateInfo.Address,
            CallTime: eventDateInfo.CallTime,
        });
        const filteredGroupedModels = sortGroupedModelsByCurrentModel(_groupedModels, selectedModel);
        setGroupedModels(filteredGroupedModels);
    }, [eventDateInfo]);

    const reloadGroupedModels = (newModel) => {
        if (newModel) {
            const newGroupedModels = groupedModels.map((groupedModel) => {
                return {
                    ...groupedModel,
                    models: groupedModel.models.map((model) => (model.ID === newModel.ID ? newModel : model)),
                };
            });
            setGroupedModels(newGroupedModels);
        }
        afterUpdate();
    };

    const removeModelFromList = (modelToRemove) => {
        const newGroupedModels = groupedModels.map((groupedModel) => {
            return {
                ...groupedModel,
                models: groupedModel.models.filter((model) => model.ID !== modelToRemove.ID),
            };
        });
        setGroupedModels(newGroupedModels);
    };

    const handleRemoveEventDatesModel = async (model) => {
        try {
            await onDeleteEventDatesModels({ ID: eventData.ID, eventDateModelId: model.ID });
            await removeModelFromList(model);
            loadEventData();
        } catch (error) {
            console.error('Remove Event Date Model Error => ', error);
        }
    };

    const handleDeleteEventDatesModels = async (models, deleteWholeDay = false) => {
        if (deleteWholeDay) {
            await onDeleteEventDate({ ID: eventData.ID, eventDateId: eventDateInfo.ID });
            loadEventData();
            return;
        }
        models.forEach((model) => {
            handleRemoveEventDatesModel(model);
        });
    };

    const onReloadEventStatus = async (model) => {
        const event = await Axios.get(`/events/${eventData.ID}`);
        const newModel = findModelInEventData(event, model.ID);
        reloadGroupedModels(newModel);
    };

    const handleViewDateModels = () => {
        setViewDateModels(true);
    };

    const handleCloseDateModels = () => {
        setViewDateModels(false);
    };

    const onEditTimeAndAddress = () => {
        onOpenEditWorkingDateDrawer({
            FromDate: eventDateInfo.FromDate,
            ToDate: eventDateInfo.ToDate,
            Address: eventDateInfo.Address,
            Notes: eventDateInfo.Notes,
            CallTime: eventDateInfo.CallTime,
            StartBreak: eventDateInfo.StartBreak,
            EndBreak: eventDateInfo.EndBreak,
            models: [],
            eventDateId: eventDateInfo.ID,
        });
    };

    if (eventDateInfo.EventDatesModels.length === 0) {
        return (
            <BookingEventModelDetailsWrapper className={eventDateInfo.Cancel ? 'is-cancelled' : null}>
                <BookingEventDateContainer>
                    {isMobile ? (
                        <HorizontalDate date={eventDateInfo.FromDate} showDay />
                    ) : (
                        <VerticalDate date={eventDateInfo.FromDate} showDay />
                    )}
                    <div className="calendar-button">
                        <BookingEventStyledCalendarEditIcon
                            icon={byPrefixAndName.fasl['calendar-edit']}
                            size="2x"
                            onClick={() => onOpenEditWorkingDayDrawer(eventDateInfo)}
                        />
                    </div>
                </BookingEventDateContainer>
                <BookingEventModelInfo>
                    <div>No models</div>
                    <BookingEventDateInfo>
                        <ResponsiveFlexContainer
                            leftMinWidth={120}
                            columns={[
                                {
                                    content: eventDateInfo.StartBreak ? (
                                        <div className="is-label">
                                            <HorizontalPeriod
                                                noPadding
                                                startDate={eventDateInfo.FromDate}
                                                endDate={eventDateInfo.StartBreak}
                                                time
                                                hideDate
                                            />
                                            <HorizontalPeriod
                                                noPadding
                                                startDate={eventDateInfo.EndBreak}
                                                endDate={eventDateInfo.ToDate}
                                                time
                                                hideDate
                                            />
                                            {eventDateInfo.CallTime && (
                                                <CallTimeContainer callTime={eventDateInfo.CallTime} />
                                            )}
                                        </div>
                                    ) : (
                                        <div className="is-label">
                                            {isMobile ? (
                                                <HorizontalPeriod
                                                    noPadding
                                                    startDate={eventDateInfo.FromDate}
                                                    endDate={eventDateInfo.ToDate}
                                                    time
                                                    hideDate
                                                />
                                            ) : (
                                                <VerticalPeriod
                                                    noPadding
                                                    startDate={eventDateInfo.FromDate}
                                                    endDate={eventDateInfo.ToDate}
                                                    time
                                                    hideDate
                                                />
                                            )}
                                            {eventDateInfo.CallTime && (
                                                <CallTimeContainer callTime={eventDateInfo.CallTime} />
                                            )}
                                        </div>
                                    ),
                                    isBorder: true,
                                },
                                {
                                    content: (
                                        <div className="is-content">
                                            <AddressLine address={eventDateInfo.Address} twoLines />
                                        </div>
                                    ),
                                    width: 'full',
                                    isBorder: false,
                                },
                                {
                                    content: isMobile ? (
                                        <div style={{ padding: '1rem 0' }}>
                                            <NwLinkButton
                                                icon={byPrefixAndName.fasl['pen']}
                                                label="Edit time and address"
                                                onClick={onEditTimeAndAddress}
                                            />
                                        </div>
                                    ) : (
                                        <NwButton icon={byPrefixAndName.fasl['pen']} onClick={onEditTimeAndAddress} />
                                    ),
                                    isBorder: false,
                                },
                            ]}
                        />
                        {eventDateInfo.Notes && (
                            <ResponsiveFlexContainer
                                leftMinWidth={120}
                                columns={[
                                    {
                                        content: <div className="is-label is-center">NOTES</div>,
                                        isBorder: true,
                                    },
                                    {
                                        content: <div className="is-content">{eventDateInfo.Notes}</div>,
                                        width: 'full',
                                        isBorder: false,
                                    },
                                ]}
                            />
                        )}
                    </BookingEventDateInfo>
                </BookingEventModelInfo>
            </BookingEventModelDetailsWrapper>
        );
    }

    return (
        <BookingEventModelDetailsWrapper className={eventDateInfo.Cancel ? 'is-cancelled' : null}>
            <BookingEventDateContainer>
                {isMobile ? (
                    <HorizontalDate date={eventDateInfo.FromDate} size="big" showDay />
                ) : (
                    <VerticalDate date={eventDateInfo.FromDate} size="big" showDay />
                )}
                {!isMobile && (
                    <div>
                        <NwIcon icon={byPrefixAndName.fasl['stream']} pointer onClick={handleViewDateModels} />
                    </div>
                )}
                <div className="calendar-button">
                    <BookingEventStyledCalendarEditIcon
                        icon={byPrefixAndName.fasl['calendar-edit']}
                        onClick={() => onOpenEditWorkingDayDrawer(eventDateInfo)}
                    />
                </div>
            </BookingEventDateContainer>
            <BookingEventModelInfo>
                {groupedModels.map((modelsGroup, index) => {
                    const currentModel = modelsGroup.models.find((m) => m.ModelID === selectedModel);
                    const otherModels = modelsGroup.models.filter((m) => m.ModelID !== selectedModel);
                    if (!currentModel && otherModels.length === 0) {
                        return null;
                    }

                    const onEditTimeAndAddress = (groupedmodels) => {
                        if (groupedmodels) {
                            onOpenEditWorkingDateDrawer({
                                FromDate: modelsGroup.groupingData.FromDate,
                                ToDate: modelsGroup.groupingData.ToDate,
                                StartBreak: modelsGroup.groupingData.StartBreak,
                                EndBreak: modelsGroup.groupingData.EndBreak,
                                CallTime: modelsGroup.groupingData.CallTime,
                                Address: modelsGroup.groupingData.Address,
                                Notes: modelsGroup.groupingData.Notes,
                                models: modelsGroup.models,
                            });
                        } else {
                            onOpenEditWorkingDateDrawer({
                                FromDate: eventDateInfo.FromDate,
                                ToDate: eventDateInfo.ToDate,
                                StartBreak: eventDateInfo.StartBreak,
                                EndBreak: eventDateInfo.EndBreak,
                                CallTime: eventDateInfo.CallTime,
                                Address: eventDateInfo.Address,
                                Notes: eventDateInfo.Notes,
                                models: modelsGroup.models,
                            });
                        }
                    };

                    const actions = [
                        {
                            name: '',
                            key: 'edit',
                            tooltip: 'Edit time and address',
                            pinned: true,
                            icon: byPrefixAndName.fasl['pen'],
                            action: () => onEditTimeAndAddress(),
                        },
                    ];

                    if (currentModel) {
                        actions.push({
                            name: getModelActionName(currentModel, eventDateInfo),
                            key: currentModel.ID.toString(),
                            type: 'delete',
                            tooltip: 'Remove model',
                            icon: byPrefixAndName.fasl['trash-alt'],
                            confirmationPlacement: 'left',
                            action: () => handleRemoveEventDatesModel(currentModel),
                        });
                    }

                    otherModels.map((model) => {
                        actions.push({
                            name: getModelActionName(model, eventDateInfo),
                            key: model.ID.toString(),
                            type: 'delete',
                            tooltip: 'Remove model',
                            icon: byPrefixAndName.fasl['trash-alt'],
                            confirmationPlacement: 'left',
                            action: () => handleRemoveEventDatesModel(model),
                        });
                        return null;
                    });

                    actions.push({
                        name: 'Remove date for all models',
                        key: 'remove for all',
                        type: 'delete',
                        tooltip: 'Remove models',
                        icon: byPrefixAndName.fasl['trash-alt'],
                        confirmationPlacement: 'left',
                        action: () => handleDeleteEventDatesModels(modelsGroup.models, groupedModels.length === 1),
                    });

                    return (
                        <div key={modelsGroup.models[0] ? modelsGroup.models[0].ID : index}>
                            {currentModel && (
                                <BookingEventSingleModelName
                                    model={currentModel}
                                    withPadding={false}
                                    withMarginTop={false}
                                    currentModel={currentModel}
                                    eventData={eventData}
                                    eventDateInfo={eventDateInfo}
                                    reloadGroupedModels={reloadGroupedModels}
                                    onExtendOptionNumberToAllDates={onExtendOptionNumberToAllDates}
                                    afterUpdate={afterUpdate}
                                    onReloadEventStatus={onReloadEventStatus}
                                />
                            )}
                            {otherModels.length > 1 && (
                                <CollapsibleModels
                                    //collapsed={modelsListCollapsed()} // {!filterModel && otherModels.length < 10}
                                    expanded={isMobile ? false : filterModel && otherModels.length < 5 ? true : false}
                                    models={otherModels}
                                    eventData={eventData}
                                    eventDateInfo={eventDateInfo}
                                    currentModel={currentModel}
                                    reloadGroupedModels={reloadGroupedModels}
                                    onExtendOptionNumberToAllDates={onExtendOptionNumberToAllDates}
                                    afterUpdate={afterUpdate}
                                />
                            )}
                            {otherModels.length === 1 && (
                                <BookingEventSingleModelName
                                    model={otherModels[0]}
                                    withPadding={false}
                                    withMarginTop={false}
                                    currentModel={currentModel}
                                    eventData={eventData}
                                    eventDateInfo={eventDateInfo}
                                    reloadGroupedModels={reloadGroupedModels}
                                    onExtendOptionNumberToAllDates={onExtendOptionNumberToAllDates}
                                    afterUpdate={afterUpdate}
                                    onReloadEventStatus={onReloadEventStatus}
                                />
                            )}
                            <BookingEventDateInfo>
                                <ResponsiveFlexContainer
                                    leftMinWidth={130}
                                    columns={[
                                        {
                                            content: modelsGroup.groupingData.StartBreak ? (
                                                <div className="is-label">
                                                    <HorizontalPeriod
                                                        noPadding
                                                        startDate={modelsGroup.groupingData.FromDate}
                                                        endDate={modelsGroup.groupingData.StartBreak}
                                                        time
                                                        hideDate
                                                    />
                                                    <HorizontalPeriod
                                                        noPadding
                                                        startDate={modelsGroup.groupingData.EndBreak}
                                                        endDate={modelsGroup.groupingData.ToDate}
                                                        time
                                                        hideDate
                                                    />
                                                    {modelsGroup.groupingData.CallTime && (
                                                        <CallTimeContainer
                                                            callTime={modelsGroup.groupingData.CallTime}
                                                        />
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="is-label">
                                                    {isMobile ? (
                                                        <HorizontalPeriod
                                                            noPadding
                                                            startDate={modelsGroup.groupingData.FromDate}
                                                            endDate={modelsGroup.groupingData.ToDate}
                                                            time
                                                            hideDate
                                                        />
                                                    ) : (
                                                        <VerticalPeriod
                                                            noPadding
                                                            startDate={modelsGroup.groupingData.FromDate}
                                                            endDate={modelsGroup.groupingData.ToDate}
                                                            time
                                                            hideDate
                                                        />
                                                    )}
                                                    {modelsGroup.groupingData.CallTime && (
                                                        <CallTimeContainer
                                                            callTime={modelsGroup.groupingData.CallTime}
                                                        />
                                                    )}
                                                </div>
                                            ),
                                            isBorder: true,
                                        },
                                        {
                                            content: (
                                                <div className="is-content">
                                                    <AddressLine address={modelsGroup.groupingData.Address} twoLines />
                                                </div>
                                            ),
                                            width: 'full',
                                            isBorder: false,
                                        },
                                        {
                                            content: isMobile ? (
                                                <div style={{ padding: '1rem 0' }}>
                                                    <NwLinkButton
                                                        icon={byPrefixAndName.fasl['pen']}
                                                        label="Edit time and address"
                                                        onClick={() => onEditTimeAndAddress(true)}
                                                    />
                                                </div>
                                            ) : (
                                                <NwButton
                                                    icon={byPrefixAndName.fasl['pen']}
                                                    onClick={() => onEditTimeAndAddress(true)}
                                                />
                                            ),
                                            isBorder: false,
                                        },
                                    ]}
                                />
                                {modelsGroup.groupingData.Notes && (
                                    <ResponsiveFlexContainer
                                        leftMinWidth={130}
                                        columns={[
                                            {
                                                content: <div className="is-label is-center">NOTES</div>,
                                                isBorder: true,
                                            },
                                            {
                                                content: (
                                                    <div className={isMobile ? 'is-content small' : 'is-content'}>
                                                        {modelsGroup.groupingData.Notes}
                                                    </div>
                                                ),
                                                width: 'full',
                                                isBorder: false,
                                            },
                                        ]}
                                    />
                                )}
                                {currentModel &&
                                    groupedModels.find(
                                        (modelGroup, groupIndex) =>
                                            groupIndex !== index &&
                                            modelGroup.models.find((model) => model.ModelID !== currentModel.ModelID)
                                    ) && <NwDivider>Other Models</NwDivider>}
                            </BookingEventDateInfo>
                        </div>
                    );
                })}
            </BookingEventModelInfo>
            {viewDateModels && (
                <NwDrawer
                    title={`${getMoment(eventDateInfo.FromDate).format('ddd Do MMM YYYY')} - ${eventData.Name}`}
                    onClose={handleCloseDateModels}
                >
                    <EventDateModel afterUpdate={afterUpdate} eventDateInfo={eventDateInfo} eventData={eventData} />
                </NwDrawer>
            )}
        </BookingEventModelDetailsWrapper>
    );
};

export default EventDatesAndModels;
